import type { UpdateLocationInput } from 'app/generated/gql/graphql'
import { useParams } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { LocationType } from 'app/types/entities'
import { showModal } from 'app/lib/modal'
import { notifySuccess } from 'app/services/notify.client'
import {
  ANY_ADA_CODE,
  filterEligibleProcedureCodesForSave,
  mapSelectionToSettings,
  mapSettingsToEligibleCodes,
  mapSettingsToSelection,
} from 'lib/newPatientRulesMappings'
import { OptionType } from 'types/OptionType'
import { CustomDentalCodeForm } from 'app/components/pms-config/CustomDentalCodeForm'
import { useLocationStore } from 'app/stores/StoreContext'

export const primaryRuleOptions: OptionType[] = [
  { id: `all-procedures`, name: `Any Procedure Transaction (Excluding Broken Dental Codes)`, value: `all-procedures` },
  { id: `ADA`, name: `Any ADA Code`, value: `ADA` },
  { id: `new-patient-codes`, name: `First Eligible Procedure Transaction`, value: `new-patient-codes` },
  { id: `first-completed-appointment`, name: `First Completed Appointment`, value: `first-completed-appointment` },
  { id: `first-visit-date`, name: `First Visit Date`, value: `first-visit-date` },
]

export { ANY_ADA_CODE } from 'lib/newPatientRulesMappings'

export const eligibleProcedureOptions: OptionType[] = [
  { id: `D0140`, name: `D0140`, value: `D0140` },
  { id: `D0145`, name: `D0145`, value: `D0145` },
  { id: `D0150`, name: `D0150`, value: `D0150` },
  { id: `D0180`, name: `D0180`, value: `D0180` },
  { id: `01101`, name: `01101`, value: `01101` },
  { id: `01103`, name: `01103`, value: `01103` },
]

export const brokenAppointmentCodeOptions: OptionType[] = [
  { id: `D9986`, name: `D9986`, value: `D9986`, meta: { isDefault: true, isDisabled: true } },
  { id: `D9987`, name: `D9987`, value: `D9987`, meta: { isDefault: true, isDisabled: true } },
  { id: `199`, name: `199`, value: `199`, meta: { isDefault: true, isDisabled: true } },
]

export const BACKEND_DEFAULT_COMPLETION_STATUSES = [`Completed`, `Complete`, `Kept`]

export const completedAppointmentStatusOptions: OptionType[] = [
  { id: `Completed`, name: `Completed`, value: `Completed`, meta: { isDefault: true, isDisabled: true } },
  { id: `Complete`, name: `Complete`, value: `Complete`, meta: { isDefault: true, isDisabled: true } },
  { id: `Kept`, name: `Kept`, value: `Kept`, meta: { isDefault: true, isDisabled: true } },
  { id: `Checked Out`, name: `Checked Out`, value: `Checked Out` },
]

export const enabledDisabledOptions: OptionType[] = [
  { id: `enabled`, name: `Enabled`, value: `true` },
  { id: `disabled`, name: `Disabled`, value: `false` },
]

export function arrayToOptions(arr: string[] | undefined, allOptions: OptionType[]): OptionType[] {
  if (!arr) return []
  return arr
    .map((val) => allOptions.find((o) => o.value === val) || { id: val, name: val, value: val })
    .filter(Boolean) as OptionType[]
}

export function mergeOptionsWithCustomCodes(
  predefinedOptions: OptionType[],
  savedCodes: string[] | undefined,
): OptionType[] {
  if (!savedCodes) return predefinedOptions
  const predefinedValues = new Set(predefinedOptions.map((o) => o.value))
  const customOptions = savedCodes
    .filter((code) => !predefinedValues.has(code))
    .map((code) => ({ id: code, name: code, value: code, meta: { isDeletable: true } }))
  return [...predefinedOptions, ...customOptions]
}

export type UsePmsConfigReturn = {
  location: LocationType | undefined
  isDirtyForm: boolean
  formRef: React.RefObject<HTMLFormElement | null>
  selectedPrimaryRules: OptionType[]
  hasAllProceduresSelected: boolean
  hasAdaSelected: boolean
  showEligibleProceduresDropdown: boolean
  handlePrimaryRuleChange: (newOptions: OptionType[]) => void
  eligibleProcedureOpts: OptionType[]
  selectedEligibleProcedures: OptionType[]
  hasAnyAdaCodeSelected: boolean
  handleEligibleProcedureChange: (newOptions: OptionType[]) => void
  selectedPediatric: OptionType | undefined
  setSelectedPediatric: (opt: OptionType) => void
  brokenCodeOpts: OptionType[]
  selectedBrokenCodes: OptionType[]
  completionStatusOpts: OptionType[]
  selectedCompletionStatuses: OptionType[]
  selectedMigrationHandling: OptionType | undefined
  setSelectedMigrationHandling: (opt: OptionType) => void
  hiddenInputs: Array<{ name: string; value: string | undefined }>
  handleAddEligibleProcedure: () => void
  handleDeleteEligibleProcedure: (option: OptionType) => void
  handleAddBrokenCode: () => void
  handleDeleteBrokenCode: (option: OptionType) => void
  handleAddCompletionStatus: () => void
  handleDeleteCompletionStatus: (option: OptionType) => void
  setSelectedBrokenCodes: React.Dispatch<React.SetStateAction<OptionType[]>>
  setSelectedCompletionStatuses: React.Dispatch<React.SetStateAction<OptionType[]>>
  setIsDirtyForm: (v: boolean) => void
  handleSaveConfiguration: () => Promise<void>
}

export function usePmsConfig(): UsePmsConfigReturn {
  const { locationId } = useParams()
  const store = useLocationStore()
  const [location, setLocation] = useState<LocationType | undefined>()

  useEffect(() => {
    if (!locationId) return
    store
      .fetchOne(locationId)
      .then((result) => {
        const data = result.data as any
        const loc = data?.location ?? data?.hits?.[0] ?? data
        if (loc) setLocation(loc as LocationType)
      })
      .catch(() => {})
  }, [locationId])

  const [isDirtyForm, setIsDirtyForm] = useState(false)
  const formRef = useRef<HTMLFormElement>(null)

  const initialPrimaryRules = useMemo(() => {
    const selectedValues = mapSettingsToSelection(location?.settings)
    return selectedValues.map((v) => primaryRuleOptions.find((o) => o.value === v)!)
  }, [location?.settings])
  const [selectedPrimaryRules, setSelectedPrimaryRules] = useState<OptionType[]>(initialPrimaryRules)

  // Sync state when location loads async
  useEffect(() => {
    if (!location) return
    const selectedValues = mapSettingsToSelection(location.settings)
    setSelectedPrimaryRules(selectedValues.map((v) => primaryRuleOptions.find((o) => o.value === v)!))

    const opts = mergeOptionsWithCustomCodes(eligibleProcedureOptions, location.settings?.newPatientCodes)
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setEligibleProcedureOpts(opts)
    const codes = mapSettingsToEligibleCodes(location.settings)
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setSelectedEligibleProcedures(arrayToOptions(codes, opts))

    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setSelectedPediatric(
      location.settings?.excludeParentsFromNewPatients ? enabledDisabledOptions[0] : enabledDisabledOptions[1],
    )

    const bOpts = mergeOptionsWithCustomCodes(brokenAppointmentCodeOptions, location.settings?.transactionBrokenCodes)
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setBrokenCodeOpts(bOpts)
    const savedBroken = location.settings?.transactionBrokenCodes
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setSelectedBrokenCodes(
      !savedBroken || savedBroken.length === 0 ? brokenAppointmentCodeOptions : arrayToOptions(savedBroken, bOpts),
    )

    const cOpts = mergeOptionsWithCustomCodes(
      completedAppointmentStatusOptions,
      location.settings?.appointmentCompletionStatuses,
    )
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setCompletionStatusOpts(cOpts)
    const savedStatuses = location.settings?.appointmentCompletionStatuses
    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setSelectedCompletionStatuses(
      !savedStatuses || savedStatuses.length === 0
        ? completedAppointmentStatusOptions.filter((o) => BACKEND_DEFAULT_COMPLETION_STATUSES.includes(o.value))
        : arrayToOptions(savedStatuses, cOpts),
    )

    // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
    setSelectedMigrationHandling(location.migrationDate ? enabledDisabledOptions[0] : enabledDisabledOptions[1])
  }, [location])

  const hasAllProceduresSelected = selectedPrimaryRules.some((r) => r.value === `all-procedures`)
  const hasEligibleProcedureSelected = selectedPrimaryRules.some((r) => r.value === `new-patient-codes`)
  const hasAdaSelected = selectedPrimaryRules.some((r) => r.value === `ADA`)
  const showEligibleProceduresDropdown = hasEligibleProcedureSelected || hasAdaSelected

  const handlePrimaryRuleChange = useCallback(
    (newOptions: OptionType[]) => {
      const EXCLUSIVE = [`all-procedures`, `ADA`, `new-patient-codes`]
      const prevExclusive = selectedPrimaryRules.find((r) => EXCLUSIVE.includes(r.value))
      const newExclusives = newOptions.filter((r) => EXCLUSIVE.includes(r.value))
      const newBooleans = newOptions.filter((r) => !EXCLUSIVE.includes(r.value))

      let finalOptions: OptionType[]

      if (newExclusives.length > 1) {
        // A new exclusive was added — keep only the newly added one + booleans
        const newlyAdded = newExclusives.find((r) => r.value !== prevExclusive?.value) || newExclusives[0]
        finalOptions = [newlyAdded, ...newBooleans]
      } else {
        finalOptions = newOptions
      }

      if (finalOptions.length === 0) finalOptions = [primaryRuleOptions[0]]
      setSelectedPrimaryRules(finalOptions)
      setIsDirtyForm(true)
    },
    [selectedPrimaryRules],
  )

  const initialEligibleOpts = mergeOptionsWithCustomCodes(eligibleProcedureOptions, location?.settings?.newPatientCodes)
  const [eligibleProcedureOpts, setEligibleProcedureOpts] = useState<OptionType[]>(initialEligibleOpts)
  const [selectedEligibleProcedures, setSelectedEligibleProcedures] = useState<OptionType[]>(() => {
    const codes = mapSettingsToEligibleCodes(location?.settings)
    return arrayToOptions(codes, initialEligibleOpts)
  })

  const hasAnyAdaCodeSelected = selectedEligibleProcedures.some((r) => r.value === ANY_ADA_CODE)

  const handleEligibleProcedureChange = useCallback((newOptions: OptionType[]) => {
    setSelectedEligibleProcedures(newOptions)
    setIsDirtyForm(true)
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [])

  const initialPediatric = location?.settings?.excludeParentsFromNewPatients
    ? enabledDisabledOptions[0]
    : enabledDisabledOptions[1]
  const [selectedPediatric, setSelectedPediatric] = useState<OptionType | undefined>(initialPediatric)

  const initialBrokenOpts = mergeOptionsWithCustomCodes(
    brokenAppointmentCodeOptions,
    location?.settings?.transactionBrokenCodes,
  )
  const [brokenCodeOpts, setBrokenCodeOpts] = useState<OptionType[]>(initialBrokenOpts)
  const [selectedBrokenCodes, setSelectedBrokenCodes] = useState<OptionType[]>(() => {
    const savedCodes = location?.settings?.transactionBrokenCodes
    if (!savedCodes || savedCodes.length === 0) return brokenAppointmentCodeOptions
    return arrayToOptions(savedCodes, initialBrokenOpts)
  })

  const initialCompletionOpts = mergeOptionsWithCustomCodes(
    completedAppointmentStatusOptions,
    location?.settings?.appointmentCompletionStatuses,
  )
  const [completionStatusOpts, setCompletionStatusOpts] = useState<OptionType[]>(initialCompletionOpts)
  const [selectedCompletionStatuses, setSelectedCompletionStatuses] = useState<OptionType[]>(() => {
    const savedStatuses = location?.settings?.appointmentCompletionStatuses
    if (!savedStatuses || savedStatuses.length === 0) {
      return completedAppointmentStatusOptions.filter((o) => BACKEND_DEFAULT_COMPLETION_STATUSES.includes(o.value))
    }
    return arrayToOptions(savedStatuses, initialCompletionOpts)
  })

  const handleAddEligibleProcedure = () => {
    showModal(
      <CustomDentalCodeForm
        placeholder="Enter Your Custom Dental Code"
        onSave={(code) => {
          const newOption: OptionType = { id: code, name: code, value: code, meta: { isDeletable: true } }
          setEligibleProcedureOpts((prev) => [...prev, newOption])
          setSelectedEligibleProcedures((prev) => [...prev, newOption])
          setIsDirtyForm(true)
        }}
      />,
      { title: `Custom Dental Code`, isFullScreen: false, removePadding: true, className: `z-50`, useRadix: true },
    )
  }

  const handleDeleteEligibleProcedure = useCallback((option: OptionType) => {
    setEligibleProcedureOpts((prev) => prev.filter((o) => o.value !== option.value))
    setSelectedEligibleProcedures((prev) => prev.filter((o) => o.value !== option.value))
    setIsDirtyForm(true)
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [])

  const handleAddBrokenCode = () => {
    showModal(
      <CustomDentalCodeForm
        placeholder="Enter Your Custom Broken Code"
        onSave={(code) => {
          const newOption: OptionType = { id: code, name: code, value: code, meta: { isDeletable: true } }
          setBrokenCodeOpts((prev) => [...prev, newOption])
          setSelectedBrokenCodes((prev) => [...prev, newOption])
          setIsDirtyForm(true)
        }}
      />,
      { title: `Custom Broken Code`, isFullScreen: false, removePadding: true, className: `z-50`, useRadix: true },
    )
  }

  const handleDeleteBrokenCode = useCallback((option: OptionType) => {
    setBrokenCodeOpts((prev) => prev.filter((o) => o.value !== option.value))
    setSelectedBrokenCodes((prev) => prev.filter((o) => o.value !== option.value))
    setIsDirtyForm(true)
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [])

  const handleAddCompletionStatus = () => {
    showModal(
      <CustomDentalCodeForm
        placeholder="Enter Your Custom Status"
        onSave={(code) => {
          const newOption: OptionType = { id: code, name: code, value: code, meta: { isDeletable: true } }
          setCompletionStatusOpts((prev) => [...prev, newOption])
          setSelectedCompletionStatuses((prev) => [...prev, newOption])
          setIsDirtyForm(true)
        }}
      />,
      {
        title: `Custom Completed Appointment Status`,
        isFullScreen: false,
        removePadding: true,
        className: `z-50`,
        useRadix: true,
      },
    )
  }

  const handleDeleteCompletionStatus = useCallback((option: OptionType) => {
    setCompletionStatusOpts((prev) => prev.filter((o) => o.value !== option.value))
    setSelectedCompletionStatuses((prev) => prev.filter((o) => o.value !== option.value))
    setIsDirtyForm(true)
    // oxlint-disable-next-line react/preserve-manual-memoization -- existing manual memoization is intentional here
  }, [])

  const [selectedMigrationHandling, setSelectedMigrationHandling] = useState<OptionType | undefined>(
    location?.migrationDate ? enabledDisabledOptions[0] : enabledDisabledOptions[1],
  )

  const handleSaveConfiguration = useCallback(async () => {
    if (!locationId || !location) return

    const eligibleCodes = selectedEligibleProcedures.map((o) => o.value)
    const primaryRules = selectedPrimaryRules.map((o) => o.value)
    const mapped = mapSelectionToSettings(primaryRules, eligibleCodes)
    // Always send an array so the API replaces the saved codes, empty when none.
    const newPatientCodes = filterEligibleProcedureCodesForSave(eligibleCodes) ?? []

    const input: UpdateLocationInput = {
      settings: {
        // preserve PII setting (not editable here)
        ignorePII: location.settings?.ignorePII,
        newPatientCalculation: mapped.newPatientCalculation,
        appointmentsSetNewPatient: mapped.appointmentsSetNewPatient,
        firstVisitSetNewPatient: mapped.firstVisitSetNewPatient,
        newPatientCodes,
        excludeParentsFromNewPatients: selectedPediatric?.value === `true`,
        transactionBrokenCodes: selectedBrokenCodes.map((o) => o.value),
        appointmentCompletionStatuses: selectedCompletionStatuses.map((o) => o.value),
      },
    }

    if (selectedMigrationHandling?.value === `true`) {
      const dateInput = formRef.current?.querySelector<HTMLInputElement>(`[name="migrationDate"]`)
      if (dateInput?.value) input.migrationDate = dateInput.value
    } else {
      input.migrationDate = null
    }

    const result = await store.update(locationId, input)
    if (result.error) return
    if (store.current) setLocation(store.current as LocationType)
    setIsDirtyForm(false)
    notifySuccess(`PMS configuration saved. Location data may take a few minutes to update.`)
  }, [
    locationId,
    location,
    selectedPrimaryRules,
    selectedEligibleProcedures,
    selectedPediatric,
    selectedBrokenCodes,
    selectedCompletionStatuses,
    selectedMigrationHandling,
    store,
    formRef,
  ])

  const hiddenInputs = [{ name: `locationId`, value: location?.id }]

  return {
    handleSaveConfiguration,
    location,
    isDirtyForm,
    formRef,
    selectedPrimaryRules,
    hasAllProceduresSelected,
    hasAdaSelected,
    showEligibleProceduresDropdown,
    handlePrimaryRuleChange,
    eligibleProcedureOpts,
    selectedEligibleProcedures,
    hasAnyAdaCodeSelected,
    handleEligibleProcedureChange,
    selectedPediatric,
    setSelectedPediatric,
    brokenCodeOpts,
    selectedBrokenCodes,
    completionStatusOpts,
    selectedCompletionStatuses,
    selectedMigrationHandling,
    setSelectedMigrationHandling,
    hiddenInputs,
    handleAddEligibleProcedure,
    handleDeleteEligibleProcedure,
    handleAddBrokenCode,
    handleDeleteBrokenCode,
    handleAddCompletionStatus,
    handleDeleteCompletionStatus,
    setSelectedBrokenCodes,
    setSelectedCompletionStatuses,
    setIsDirtyForm,
  }
}
